import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../components-v2/Theme/theme';

/** Marks the layers the tile fades in on hover or focus. Paired with the selectors on `tile`. */
export const REVEAL_CLASS = 'media-tile-reveal';

const EASE = 'cubic-bezier(.4, 0, .2, 1)';
const DURATION_FAST = '140ms';

/** One tile edge. Four fit a row in the composer, large enough to judge a crop at a glance. */
const MEDIA_TILE_SIZE = 240;
const MEDIA_TILE_GAP = SPACE[8];
/** A video publishes on its own, so its tile takes the room two images would. */
const MEDIA_TILE_WIDE_SIZE = MEDIA_TILE_SIZE * 2 + MEDIA_TILE_GAP;
/** Past this the grid scrolls, so adding media never pushes the composer's controls off screen. */
const VISIBLE_ROWS = 2;
/** Room for a focus ring on the outermost tiles, which the scroll container would otherwise clip. */
const FOCUS_RING_GUTTER = SPACE[4];

/** Tilt on a dragged tile. The copy under the pointer and the tile left in the grid lean alike. */
export const DRAG_TILT_DEGREES = 4;
const DRAG_TILT_RADIANS = (DRAG_TILT_DEGREES * Math.PI) / 180;
/**
 * Shrinks the tilted tile to fit its own slot. A tilt widens a square's bounding box by the angle's
 * cosine plus sine, which overruns the grid's narrow gutter: the corners are cut, and the extra height
 * gives the grid a scrollbar mid-drag that can wrap the row under the pointer. Rounded down so it fits.
 */
const DRAG_TILT_SCALE = Math.floor(1000 / (Math.cos(DRAG_TILT_RADIANS) + Math.sin(DRAG_TILT_RADIANS))) / 1000;

// Scrims over arbitrary artwork, at the opacities that keep a control legible over both a pale photo
// and a dark one.
const scrim = (opacityPercent: number) =>
	`color-mix(in srgb, ${THEME.colors.chart.topHatBlack} ${opacityPercent}%, transparent)`;
const TILE_SCRIM = `linear-gradient(to top, ${scrim(72)}, transparent)`;
const TILE_SCRIM_STRONG = scrim(78);
const TILE_ACTION_SURFACE = `color-mix(in srgb, ${THEME.colors.background.light} 94%, transparent)`;

const focusRing = {
	outline: `2px solid ${THEME.colors.primary.main}`,
	outlineOffset: 1,
};

export const styleSheet = StyleSheet.create({
	mediaHeader: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[16],
		justifyContent: 'space-between',
		marginBottom: SPACE[8],
		minHeight: 32,
	},
	mediaLabel: {
		margin: 0,
	},

	grid: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: MEDIA_TILE_GAP,
		listStyle: 'none',
		margin: 0,
		maxHeight: (MEDIA_TILE_SIZE + MEDIA_TILE_GAP) * VISIBLE_ROWS - MEDIA_TILE_GAP + FOCUS_RING_GUTTER * 2,
		overflowX: 'hidden',
		overflowY: 'auto',
		padding: FOCUS_RING_GUTTER,
	},

	tile: {
		// Revealed through CSS rather than React state: the composer re-renders often, and a
		// state-driven class restarts the fade every time it does, so the controls never settle.
		[`:hover .${REVEAL_CLASS}`]: { opacity: 1 },
		[`:focus-within .${REVEAL_CLASS}`]: { opacity: 1 },
		background: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		flex: '0 0 auto',
		height: MEDIA_TILE_SIZE,
		overflow: 'hidden',
		position: 'relative',
		transition: `border-color ${DURATION_FAST} ${EASE}, box-shadow ${DURATION_FAST} ${EASE}, opacity ${DURATION_FAST} ${EASE}, transform ${DURATION_FAST} ${EASE}`,
		width: MEDIA_TILE_SIZE,
	},
	tileWide: {
		width: MEDIA_TILE_WIDE_SIZE,
	},
	/**
	 * The tile being dragged keeps its slot, so the row does not reflow under the pointer, and lifts out
	 * of it: it tilts, casts a shadow, and keeps most of its opacity, so it reads as held, not disabled.
	 */
	tileDragging: {
		// Small enough to fade out inside the grid's gutter, which would cut a larger one off square.
		boxShadow: `0 2px 6px ${scrim(24)}`,
		opacity: 0.9,
		transform: `rotate(-${DRAG_TILT_DEGREES}deg) scale(${DRAG_TILT_SCALE})`,
		// Above its neighbours, so its shadow falls on them rather than under them.
		zIndex: 1,
		'@media (prefers-reduced-motion: reduce)': {
			// The shadow and the fade still mark the tile as held. Only the movement goes.
			transform: 'none',
		},
	},
	tileDropTarget: {
		boxShadow: `inset 3px 0 0 0 ${THEME.colors.primary.main}`,
	},
	// Overrides Button's own box: the thumbnail is the button, edge to edge.
	tileBody: {
		borderRadius: 0,
		cursor: 'pointer',
		display: 'block',
		height: '100%',
		paddingBottom: 0,
		paddingLeft: 0,
		paddingRight: 0,
		paddingTop: 0,
		width: '100%',
		':hover': {
			background: 'none',
		},
		':focus-visible': focusRing,
	},
	tileImage: {
		display: 'block',
		height: '100%',
		objectFit: 'cover',
		width: '100%',
	},
	tileVideo: {
		background: THEME.colors.background.dark,
		display: 'block',
		height: '100%',
		objectFit: 'contain',
		width: '100%',
	},
	tileBadge: {
		background: TILE_SCRIM_STRONG,
		borderRadius: THEME.common.borderRadiusSm,
		color: THEME.colors.primary.contrastText,
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.8,
		padding: `0 ${SPACE[8]}px`,
		pointerEvents: 'none',
		position: 'absolute',
		right: SPACE[8],
		top: SPACE[8],
	},
	tileDraggable: {
		cursor: 'grab',
		':active': {
			cursor: 'grabbing',
		},
	},
	tileDragHandle: {
		alignItems: 'center',
		background: TILE_ACTION_SURFACE,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		color: THEME.colors.text.light,
		cursor: 'grab',
		display: 'flex',
		height: 24,
		justifyContent: 'center',
		left: '50%',
		position: 'absolute',
		top: SPACE[4],
		transform: 'translateX(-50%)',
		width: 36,
		zIndex: 1,
		':active': {
			cursor: 'grabbing',
		},
	},
	tileDragHandleIcon: {
		height: 18,
		transform: 'rotate(90deg)',
		width: 18,
	},

	/** Marks the thumbnail as the way to enlarge, which nothing else on the tile says. */
	tileZoomCue: {
		alignItems: 'center',
		background: TILE_SCRIM_STRONG,
		borderRadius: '50%',
		color: THEME.colors.primary.contrastText,
		display: 'flex',
		height: 44,
		justifyContent: 'center',
		left: '50%',
		opacity: 0,
		pointerEvents: 'none',
		position: 'absolute',
		top: '50%',
		transform: 'translate(-50%, -50%)',
		transition: `opacity ${DURATION_FAST} ${EASE}`,
		width: 44,
	},

	/** Held off the thumbnail until the tile is hovered or something inside it takes focus. */
	tileActions: {
		alignItems: 'flex-end',
		backgroundImage: TILE_SCRIM,
		bottom: 0,
		display: 'flex',
		gap: SPACE[8],
		height: '40%',
		justifyContent: 'center',
		left: 0,
		opacity: 0,
		padding: SPACE[8],
		// The bar covers part of the thumbnail, which is itself the enlarge control. Only the buttons
		// take clicks, so the rest of the tile keeps working through it.
		pointerEvents: 'none',
		position: 'absolute',
		right: 0,
		transition: `opacity ${DURATION_FAST} ${EASE}`,
	},
	tileAction: {
		background: TILE_ACTION_SURFACE,
		borderRadius: THEME.common.borderRadiusSm,
		color: THEME.colors.text.main,
		cursor: 'pointer',
		height: 34,
		pointerEvents: 'auto',
		paddingBottom: 0,
		paddingLeft: 0,
		paddingRight: 0,
		paddingTop: 0,
		width: 34,
		':hover': {
			background: THEME.colors.background.light,
		},
		':focus-visible': focusRing,
	},
	tileActionDestructive: {
		color: THEME.colors.danger.main,
	},
	addBlockedNote: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		marginTop: SPACE[8],
	},

	addTile: {
		background: 'none',
		border: `1px dashed ${THEME.colors.border.main}`,
		color: THEME.colors.text.light,
		cursor: 'pointer',
		flexDirection: 'column',
		gap: SPACE[8],
		fontSize: THEME.typography.fontSizes.sm,
		height: MEDIA_TILE_SIZE,
		paddingBottom: 0,
		paddingLeft: 0,
		paddingRight: 0,
		paddingTop: 0,
		width: MEDIA_TILE_SIZE,
		':hover': {
			background: THEME.colors.background.main,
			borderColor: THEME.colors.primary.main,
			color: THEME.colors.primary.main,
		},
		':focus-visible': focusRing,
	},
	addTileDisabled: {
		cursor: 'not-allowed',
		opacity: 0.5,
		':hover': {
			background: 'none',
			borderColor: THEME.colors.border.main,
			color: THEME.colors.text.light,
		},
	},
	addTileIcon: {
		height: 28,
		width: 28,
	},

	srOnly: {
		border: 0,
		clip: 'rect(0 0 0 0)',
		height: 1,
		margin: -1,
		overflow: 'hidden',
		padding: 0,
		position: 'absolute',
		whiteSpace: 'nowrap',
		width: 1,
	},
});
